import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Tag,
  TagLabel,
  Code,
  Flex,
} from '@chakra-ui/react';
import { AlertTriangle, AlertCircle, CheckCircle2, ShieldAlert, Info } from 'lucide-react';
import type {
  ValidationReport,
  ValidationStatus,
  ValidationSeverity,
  ValidatorResult,
} from '../lib/supabase';

interface ComplianceReportProps {
  report: ValidationReport;
}

const STATUS_LABELS: Record<ValidationStatus, string> = {
  pass: 'All validators passed',
  warn: 'Minor issues — review before publishing',
  error: 'Blocking issues — fix before publishing',
};

const STATUS_COLOR: Record<ValidationStatus, string> = {
  pass: 'green',
  warn: 'yellow',
  error: 'red',
};

const SEVERITY_COLOR: Record<ValidationSeverity, string> = {
  error: 'red',
  warning: 'yellow',
  info: 'blue',
};

function StatusIcon({ status }: { status: ValidationStatus }) {
  if (status === 'pass') return <CheckCircle2 size={18} />;
  if (status === 'warn') return <AlertTriangle size={18} />;
  return <AlertCircle size={18} />;
}

function SeverityIcon({ severity }: { severity: ValidationSeverity }) {
  if (severity === 'error') return <AlertCircle size={14} />;
  if (severity === 'warning') return <AlertTriangle size={14} />;
  return <Info size={14} />;
}

function HumanizedValidatorName({ raw }: { raw: string }) {
  const map: Record<string, string> = {
    structure: 'Structure',
    ban_list: 'Banned phrases',
    source_citation: 'Source citations',
    voice: 'Brand voice',
    production: 'Production formatting',
  };
  return <span>{map[raw] ?? raw.replace(/_/g, ' ')}</span>;
}

function ValidatorBlock({ validator }: { validator: ValidatorResult }) {
  const issueCount = validator.issues.length;
  const errs = validator.issues.filter((i) => i.severity === 'error').length;
  const warns = validator.issues.filter((i) => i.severity === 'warning').length;

  return (
    <AccordionItem
      borderWidth={1}
      borderColor="gray.200"
      borderRadius="md"
      mb={2}
      bg="white"
    >
      <h2>
        <AccordionButton _expanded={{ bg: 'gray.50' }}>
          <Flex flex="1" align="center" gap={2}>
            <Box color={`${STATUS_COLOR[validator.status]}.500`}>
              <StatusIcon status={validator.status} />
            </Box>
            <Text fontWeight="600" fontSize="sm">
              <HumanizedValidatorName raw={validator.name} />
            </Text>
            <Badge colorScheme={STATUS_COLOR[validator.status]} fontSize="xs">
              {validator.status}
            </Badge>
            {issueCount > 0 && (
              <Text fontSize="xs" color="gray.600">
                {errs > 0 && (
                  <Tag size="sm" colorScheme="red" mr={1}>
                    <TagLabel>{errs} error{errs === 1 ? '' : 's'}</TagLabel>
                  </Tag>
                )}
                {warns > 0 && (
                  <Tag size="sm" colorScheme="yellow">
                    <TagLabel>{warns} warning{warns === 1 ? '' : 's'}</TagLabel>
                  </Tag>
                )}
              </Text>
            )}
          </Flex>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel pb={3} pt={2}>
        {issueCount === 0 ? (
          <Text fontSize="sm" color="green.600">
            No issues detected.
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {validator.issues.map((issue, idx) => (
              <Box
                key={idx}
                p={3}
                bg={`${SEVERITY_COLOR[issue.severity]}.50`}
                borderLeftWidth={3}
                borderLeftColor={`${SEVERITY_COLOR[issue.severity]}.400`}
                borderRadius="md"
              >
                <HStack align="flex-start" spacing={2}>
                  <Box color={`${SEVERITY_COLOR[issue.severity]}.600`} mt="2px">
                    <SeverityIcon severity={issue.severity} />
                  </Box>
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" color="gray.800">
                      {issue.message}
                    </Text>
                    <HStack mt={1} spacing={2} flexWrap="wrap">
                      {issue.code && (
                        <Code fontSize="xs" colorScheme="gray">
                          {issue.code}
                        </Code>
                      )}
                      {issue.location && (
                        <Text fontSize="xs" color="gray.500">
                          {issue.location}
                        </Text>
                      )}
                    </HStack>
                  </Box>
                </HStack>
              </Box>
            ))}
          </VStack>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}

export default function ComplianceReport({ report }: ComplianceReportProps) {
  const overallColor = STATUS_COLOR[report.overall_status];
  const formattedDate = report.generated_at
    ? new Date(report.generated_at).toLocaleString()
    : null;

  // Sort validators: errors first, then warnings, then passes — reviewers
  // care most about the failures. Stable secondary sort by name.
  const sortedValidators = [...report.validators].sort((a, b) => {
    const order: Record<ValidationStatus, number> = { error: 0, warn: 1, pass: 2 };
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return a.name.localeCompare(b.name);
  });

  // Default-expanded indexes: only validators that have at least one ERROR.
  // Warning-only and pass validators stay collapsed so a draft with a few
  // advisory warnings doesn't open four accordions and bury the content
  // below the fold. The accordion header still shows the warning count
  // badge, so reviewers know there's something to expand if they care.
  // (Reviewers can also click any header to expand on demand.)
  const defaultIndex = sortedValidators
    .map((v, i) =>
      v.issues.some((issue) => issue.severity === 'error') ? i : -1,
    )
    .filter((i) => i >= 0);

  return (
    <Box>
      {/* Summary banner */}
      <Box
        p={3}
        bg={`${overallColor}.50`}
        borderRadius="lg"
        borderWidth={1}
        borderColor={`${overallColor}.200`}
        mb={3}
      >
        <HStack spacing={3} align="center">
          <Box color={`${overallColor}.600`}>
            <ShieldAlert size={22} />
          </Box>
          <Box flex="1" minW={0}>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontWeight="700" fontSize="sm" color={`${overallColor}.800`}>
                {STATUS_LABELS[report.overall_status]}
              </Text>
              <Badge colorScheme={overallColor} textTransform="uppercase">
                {report.overall_status}
              </Badge>
            </HStack>
            <HStack mt={1} spacing={3} fontSize="xs" color="gray.600" flexWrap="wrap">
              <Text>
                <strong>{report.error_count}</strong> error
                {report.error_count === 1 ? '' : 's'}
              </Text>
              <Text>
                <strong>{report.warning_count}</strong> warning
                {report.warning_count === 1 ? '' : 's'}
              </Text>
              <Text>
                Framework:{' '}
                <Code fontSize="xs">
                  {report.framework_id}
                  {report.framework_version ? ` v${report.framework_version}` : ''}
                </Code>
              </Text>
              {formattedDate && <Text>Validated {formattedDate}</Text>}
            </HStack>
          </Box>
        </HStack>
      </Box>

      <Accordion defaultIndex={defaultIndex} allowMultiple>
        {sortedValidators.map((v) => (
          <ValidatorBlock key={v.name} validator={v} />
        ))}
      </Accordion>
    </Box>
  );
}
